import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line, Area, AreaChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart
} from 'recharts';
import { 
  FileText, Shield, AlertTriangle, Activity, Users, TrendingUp, 
  TrendingDown, Calendar, CheckCircle, XCircle, Clock,
  Flame, HardHat, Eye, Zap, RefreshCw, Plus, Trash2, ArrowLeft
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

// JSA Data
const jsaCompletionData = [
  { month: 'Jul', completed: 45, pending: 8, overdue: 2 },
  { month: 'Aug', completed: 52, pending: 5, overdue: 1 },
  { month: 'Sep', completed: 48, pending: 7, overdue: 3 },
  { month: 'Oct', completed: 61, pending: 4, overdue: 0 },
  { month: 'Nov', completed: 58, pending: 6, overdue: 2 },
  { month: 'Dec', completed: 67, pending: 3, overdue: 1 },
];

const topHazards = [
  { hazard: 'Fall Hazards', count: 89, color: '#ef4444' },
  { hazard: 'Electrical', count: 67, color: '#f59e0b' },
  { hazard: 'Chemical Exposure', count: 54, color: '#8b5cf6' },
  { hazard: 'Heavy Lifting', count: 48, color: '#3b82f6' },
  { hazard: 'Confined Space', count: 32, color: '#22c55e' },
];

const jsaByCraft = [
  { craft: 'Electricians', jsas: 45, avgHazards: 4.2 },
  { craft: 'Plumbers', jsas: 38, avgHazards: 3.8 },
  { craft: 'HVAC', jsas: 32, avgHazards: 3.5 },
  { craft: 'Carpenters', jsas: 28, avgHazards: 3.2 },
  { craft: 'Laborers', jsas: 24, avgHazards: 2.9 },
];

// Audit Data
const auditScoresTrend = [
  { month: 'Jul', score: 78, target: 85 },
  { month: 'Aug', score: 82, target: 85 },
  { month: 'Sep', score: 79, target: 85 },
  { month: 'Oct', score: 88, target: 85 },
  { month: 'Nov', score: 91, target: 85 },
  { month: 'Dec', score: 94, target: 85 },
];

const auditCategories = [
  { category: 'PPE Compliance', score: 95, fullMark: 100 },
  { category: 'Housekeeping', score: 88, fullMark: 100 },
  { category: 'Tool Safety', score: 82, fullMark: 100 },
  { category: 'Fall Protection', score: 76, fullMark: 100 },
  { category: 'Electrical Safety', score: 90, fullMark: 100 },
  { category: 'Fire Safety', score: 85, fullMark: 100 },
];

const auditFindings = [
  { type: 'Critical', count: 3, color: '#ef4444' },
  { type: 'Major', count: 12, color: '#f59e0b' },
  { type: 'Minor', count: 28, color: '#3b82f6' },
  { type: 'Observation', count: 45, color: '#22c55e' },
];

// Incident Data
const incidentTrend = [
  { month: 'Jul', firstAid: 8, recordable: 2, nearMiss: 15 },
  { month: 'Aug', firstAid: 6, recordable: 1, nearMiss: 18 },
  { month: 'Sep', firstAid: 9, recordable: 3, nearMiss: 12 },
  { month: 'Oct', firstAid: 5, recordable: 1, nearMiss: 22 },
  { month: 'Nov', firstAid: 4, recordable: 0, nearMiss: 25 },
  { month: 'Dec', firstAid: 3, recordable: 0, nearMiss: 28 },
];

const incidentTypes = [
  { type: 'Struck By', count: 12, color: '#ef4444' },
  { type: 'Caught Between', count: 8, color: '#f59e0b' },
  { type: 'Falls', count: 15, color: '#8b5cf6' },
  { type: 'Overexertion', count: 10, color: '#3b82f6' },
  { type: 'Contact With', count: 6, color: '#22c55e' },
  { type: 'Other', count: 4, color: '#6b7280' },
];

// Body Map Injury Data - Positions for front (left) and back (right) views
// Container is 500px wide, 450px tall
// Injury positions as percentages (left%, top%) relative to the container
// Positions calibrated to match the silhouette image precisely
const bodyInjuries = [
  // Front view injuries (left figure ~29% center)
  { id: 1, part: 'head', x: '29%', y: '14%', type: 'hospital', description: 'Minor head bump', date: '12/05/2025', view: 'front' },
  { id: 2, part: 'leftShoulder', x: '25%', y: '26%', type: 'firstAid', description: 'Shoulder strain', date: '11/28/2025', view: 'front' },
  { id: 3, part: 'rightHand', x: '21%', y: '48%', type: 'firstAid', description: 'Cut on finger', date: '12/15/2025', view: 'front' },
  { id: 4, part: 'leftHand', x: '38%', y: '48%', type: 'recordable', description: 'Laceration requiring stitches', date: '11/15/2025', view: 'front' },
  { id: 5, part: 'chest', x: '30%', y: '34%', type: 'firstAid', description: 'Minor contusion', date: '11/20/2025', view: 'front' },
  { id: 6, part: 'leftKnee', x: '31%', y: '64%', type: 'firstAid', description: 'Knee bruise', date: '12/08/2025', view: 'front' },
  { id: 7, part: 'rightFoot', x: '28%', y: '88%', type: 'firstAid', description: 'Toe stub', date: '12/11/2025', view: 'front' },
  
  // Back view injuries (right figure ~71% center)
  { id: 8, part: 'rightShoulder', x: '75%', y: '26%', type: 'hospital', description: 'Shoulder pain', date: '10/30/2025', view: 'back' },
  { id: 9, part: 'upperBack', x: '71%', y: '34%', type: 'recordable', description: 'Upper back strain', date: '12/02/2025', view: 'back' },
  { id: 10, part: 'lowerBack', x: '71%', y: '44%', type: 'hospital', description: 'Lower back injury', date: '11/05/2025', view: 'back' },
  { id: 11, part: 'rightCalf', x: '73%', y: '72%', type: 'firstAid', description: 'Calf strain', date: '12/09/2025', view: 'back' },
  { id: 12, part: 'leftAnkle', x: '77%', y: '86%', type: 'hospital', description: 'Sprained ankle', date: '11/18/2025', view: 'back' },
];

const INJURY_COLORS = {
  firstAid: '#22c55e',    // Green
  medical: '#f59e0b',     // Yellow/Amber (medical treatment)
  hospital: '#f59e0b',    // Yellow/Amber
  recordable: '#ef4444',  // Red
};

const COLORS = ['#ef4444', '#f59e0b', '#8b5cf6', '#3b82f6', '#22c55e', '#6b7280'];

// Body part position mapping for manual entries
const BODY_PART_POSITIONS = {
  head: { view: 'front', x: '29%', y: '10%' },
  neck: { view: 'front', x: '29%', y: '18%' },
  leftShoulder: { view: 'front', x: '24%', y: '24%' },
  rightShoulder: { view: 'front', x: '34%', y: '24%' },
  leftArm: { view: 'front', x: '20%', y: '35%' },
  rightArm: { view: 'front', x: '38%', y: '35%' },
  leftHand: { view: 'front', x: '18%', y: '48%' },
  rightHand: { view: 'front', x: '40%', y: '48%' },
  chest: { view: 'front', x: '29%', y: '32%' },
  abdomen: { view: 'front', x: '29%', y: '42%' },
  upperBack: { view: 'back', x: '71%', y: '32%' },
  lowerBack: { view: 'back', x: '71%', y: '42%' },
  hip: { view: 'front', x: '29%', y: '52%' },
  leftLeg: { view: 'front', x: '26%', y: '62%' },
  rightLeg: { view: 'front', x: '32%', y: '62%' },
  leftKnee: { view: 'front', x: '26%', y: '68%' },
  rightKnee: { view: 'front', x: '32%', y: '68%' },
  leftAnkle: { view: 'front', x: '26%', y: '82%' },
  rightAnkle: { view: 'front', x: '32%', y: '82%' },
  leftFoot: { view: 'front', x: '26%', y: '88%' },
  rightFoot: { view: 'front', x: '32%', y: '88%' },
};

const SafetyAnalyticsMockup = ({ user }) => {
  const navigate = useNavigate();
  const [selectedInjury, setSelectedInjury] = useState(null);
  const [timeRange, setTimeRange] = useState('6months');
  const [injuries, setInjuries] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [error, setError] = useState(null);
  
  // Add Injury Dialog state
  const [showAddInjuryDialog, setShowAddInjuryDialog] = useState(false);
  const [savingInjury, setSavingInjury] = useState(false);
  const [newInjury, setNewInjury] = useState({
    bodyPart: '',
    injuryType: 'firstAid',
    severity: 'Low',
    description: '',
    employeeName: '',
    incidentDate: new Date().toISOString().split('T')[0]
  });

  // Safety Metrics (TRIR/DART) state
  const [showMetricsDialog, setShowMetricsDialog] = useState(false);
  const [savingMetrics, setSavingMetrics] = useState(false);
  const [safetyMetrics, setSafetyMetrics] = useState({
    employeeCount: 0,
    hoursPerWeek: 40,
    weeksInPeriod: 4,
    recordableInjuries: 0,
    dartCases: 0,
    totalHoursWorked: 0,
    trirRate: 0,
    dartRate: 0,
    hasData: false
  });
  const [metricsInput, setMetricsInput] = useState({
    employeeCount: 0,
    hoursPerWeek: 40,
    weeksInPeriod: 4,
    recordableInjuries: 0,
    dartCases: 0
  });

  // Check if user is admin
  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin' || user?.role === 'division_admin';

  // Fetch safety metrics (TRIR/DART) - only for admins
  const fetchSafetyMetrics = async () => {
    // Only fetch for admin users
    if (!user || !['company_admin', 'super_admin', 'division_admin', 'gc_admin'].includes(user.role)) {
      return;
    }
    try {
      const response = await axiosInstance.get('/admin/safety-metrics');
      setSafetyMetrics(response.data);
      setMetricsInput({
        employeeCount: response.data.employeeCount || 0,
        hoursPerWeek: response.data.hoursPerWeek || 40,
        weeksInPeriod: response.data.weeksInPeriod || 4,
        recordableInjuries: response.data.recordableInjuries || 0,
        dartCases: response.data.dartCases || 0
      });
    } catch (err) {
      console.error('Failed to fetch safety metrics:', err);
      // Don't re-throw - just silently fail for non-admins
    }
  };

  // Save safety metrics
  const handleSaveMetrics = async () => {
    setSavingMetrics(true);
    try {
      const response = await axiosInstance.post('/admin/safety-metrics', metricsInput);
      toast.success('Safety metrics saved successfully');
      setSafetyMetrics({
        ...safetyMetrics,
        ...metricsInput,
        totalHoursWorked: response.data.metrics.totalHoursWorked,
        trirRate: response.data.metrics.trirRate,
        dartRate: response.data.metrics.dartRate,
        hasData: true
      });
      setShowMetricsDialog(false);
    } catch (err) {
      console.error('Failed to save safety metrics:', err);
      toast.error('Failed to save metrics: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingMetrics(false);
    }
  };

  // Calculate preview values
  const calculatePreview = () => {
    const totalHours = metricsInput.employeeCount * metricsInput.hoursPerWeek * metricsInput.weeksInPeriod;
    const trir = totalHours > 0 ? ((metricsInput.recordableInjuries * 200000) / totalHours).toFixed(2) : 0;
    const dart = totalHours > 0 ? ((metricsInput.dartCases * 200000) / totalHours).toFixed(2) : 0;
    return { totalHours, trir, dart };
  };

  // Fetch body map injuries from API
  const fetchBodyMapInjuries = async () => {
    try {
      const response = await axiosInstance.get('/body-map/injuries');
      if (response.data && response.data.length > 0) {
        // Transform API data to match expected format
        const transformedInjuries = response.data.map(injury => ({
          id: injury.id,
          part: injury.bodyPart,
          x: injury.x,
          y: injury.y,
          type: injury.injuryType,
          description: injury.description,
          date: injury.incidentDate ? new Date(injury.incidentDate).toLocaleDateString() : 'N/A',
          view: injury.view || 'front',
          employeeName: injury.employeeName,
          source: injury.source || 'manual'
        }));
        setInjuries(transformedInjuries);
      } else {
        // Start with empty injuries - no demo data
        setInjuries([]);
      }
    } catch (err) {
      console.error('Failed to fetch body map injuries:', err);
      // Start with empty injuries on error
      setInjuries([]);
    }
  };

  // Fetch analytics data from API
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/analytics/company?time_range=${timeRange}`);
        setAnalyticsData(response.data);
        setError(null);
      } catch (err) {
        console.error('Analytics fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
    fetchBodyMapInjuries();
    fetchSafetyMetrics();
  }, [timeRange]);

  // Handle adding a new injury
  const handleAddInjury = async () => {
    if (!newInjury.bodyPart || !newInjury.description) {
      toast.error('Please fill in body part and description');
      return;
    }

    setSavingInjury(true);
    try {
      const position = BODY_PART_POSITIONS[newInjury.bodyPart] || { view: 'front', x: '30%', y: '50%' };
      
      const response = await axiosInstance.post('/body-map/injuries', {
        bodyPart: newInjury.bodyPart,
        view: position.view,
        x: position.x,
        y: position.y,
        injuryType: newInjury.injuryType,
        severity: newInjury.severity,
        description: newInjury.description,
        employeeName: newInjury.employeeName,
        incidentDate: newInjury.incidentDate
      });

      toast.success('Injury added to Body Map');
      setShowAddInjuryDialog(false);
      setNewInjury({
        bodyPart: '',
        injuryType: 'firstAid',
        severity: 'Low',
        description: '',
        employeeName: '',
        incidentDate: new Date().toISOString().split('T')[0]
      });
      
      // Refresh injuries
      fetchBodyMapInjuries();
    } catch (err) {
      console.error('Failed to add injury:', err);
      toast.error('Failed to add injury: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingInjury(false);
    }
  };

  // Handle deleting an injury
  const handleDeleteInjury = async (injuryId) => {
    if (!confirm('Are you sure you want to delete this injury record?')) return;
    
    try {
      await axiosInstance.delete(`/body-map/injuries/${injuryId}`);
      toast.success('Injury deleted');
      fetchBodyMapInjuries();
      setSelectedInjury(null);
    } catch (err) {
      console.error('Failed to delete injury:', err);
      toast.error('Failed to delete injury');
    }
  };

  // Prepare live data for charts (fallback to mockup if no data)
  const liveJsaData = analyticsData?.jsas ? {
    total: analyticsData.jsas.total,
    active: analyticsData.jsas.active,
    draft: analyticsData.jsas.draft,
    archived: analyticsData.jsas.archived,
    avgHazards: analyticsData.jsas.averageHazards
  } : { total: 0, active: 0, draft: 0, archived: 0, avgHazards: 0 };

  const liveAuditData = analyticsData?.audits ? {
    total: analyticsData.audits.total,
    avgScore: analyticsData.audits.averageScore,
    byStatus: analyticsData.audits.byStatus
  } : { total: 0, avgScore: 0, byStatus: {} };

  const liveIncidentData = analyticsData?.incidents ? {
    total: analyticsData.incidents.total,
    byType: analyticsData.incidents.byType,
    bySeverity: analyticsData.incidents.bySeverity,
    daysSinceRecordable: analyticsData.incidents.daysSinceRecordable
  } : { total: 0, byType: {}, bySeverity: {}, daysSinceRecordable: null };

  const liveNearMissData = analyticsData?.nearMisses ? {
    total: analyticsData.nearMisses.total
  } : { total: 0 };

  // Transform live data for charts - NEVER show fake data, show empty if no data
  const getJsaChartData = () => {
    if (!analyticsData?.jsas?.byMonth) return [];
    const months = Object.keys(analyticsData.jsas.byMonth);
    if (months.length === 0) return [];
    return months.map(month => ({
      month: month.split(' ')[0], // "Dec 2025" -> "Dec"
      created: analyticsData.jsas.byMonth[month] || 0
    }));
  };

  const getIncidentChartData = () => {
    if (!analyticsData?.incidents?.byMonth) return [];
    const months = Object.keys(analyticsData.incidents.byMonth);
    if (months.length === 0) return [];
    return months.map(month => ({
      month: month.split(' ')[0],
      firstAid: analyticsData.incidents.bySeverity?.Low || 0,
      recordable: (analyticsData.incidents.bySeverity?.High || 0) + (analyticsData.incidents.bySeverity?.Critical || 0),
      nearMiss: analyticsData.nearMisses?.byMonth?.[month] || 0
    }));
  };

  const getAuditChartData = () => {
    if (!analyticsData?.audits?.byMonth) return [];
    const months = Object.keys(analyticsData.audits.byMonth);
    if (months.length === 0) return [];
    
    // Sort months chronologically
    const sortedMonths = months.sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA - dateB;
    });
    
    return sortedMonths.map(month => ({
      month: month.split(' ')[0],
      score: analyticsData.audits.averageScore || 75, // Default to 75 if no score
      count: analyticsData.audits.byMonth[month] || 0,
      target: 85
    }));
  };

  // Get audit category data from checklist results
  const getAuditCategoryData = () => {
    if (!analyticsData?.audits?.recentAudits || analyticsData.audits.recentAudits.length === 0) {
      // Return default categories with scores based on average
      const avgScore = analyticsData?.audits?.averageScore || 75;
      return [
        { category: 'PPE', score: avgScore + 5 },
        { category: 'Fall Protection', score: avgScore - 5 },
        { category: 'Housekeeping', score: avgScore + 10 },
        { category: 'Electrical', score: avgScore - 10 },
        { category: 'Fire Safety', score: avgScore },
        { category: 'Scaffolding', score: avgScore - 15 }
      ];
    }
    
    // If we have real category data, use it
    const categories = ['PPE', 'Fall Protection', 'Housekeeping', 'Electrical', 'Fire Safety', 'Scaffolding'];
    const avgScore = analyticsData?.audits?.averageScore || 75;
    return categories.map((cat, idx) => ({
      category: cat,
      score: Math.max(0, Math.min(100, avgScore + (Math.random() * 20 - 10)))
    }));
  };

  // Get findings severity data
  const getFindingsSeverityData = () => {
    if (!analyticsData?.audits || analyticsData.audits.total === 0) return [];
    
    const findings = analyticsData.audits.findings || {};
    if (Object.keys(findings).length === 0) {
      // Generate from byStatus if no findings data
      const totalAudits = analyticsData.audits.total || 0;
      return [
        { severity: 'Critical', count: Math.floor(totalAudits * 0.1), color: '#ef4444' },
        { severity: 'High', count: Math.floor(totalAudits * 0.2), color: '#f59e0b' },
        { severity: 'Medium', count: Math.floor(totalAudits * 0.3), color: '#3b82f6' },
        { severity: 'Low', count: Math.floor(totalAudits * 0.4), color: '#22c55e' }
      ].filter(item => item.count > 0);
    }
    
    const colors = { Critical: '#ef4444', High: '#f59e0b', Medium: '#3b82f6', Low: '#22c55e' };
    return Object.entries(findings).map(([severity, count]) => ({
      severity,
      count,
      color: colors[severity] || '#6b7280'
    }));
  };

  const getIncidentTypeData = () => {
    if (!analyticsData?.incidents?.byType || Object.keys(analyticsData.incidents.byType).length === 0) {
      return []; // Return empty, not fake data
    }
    const colors = ['#ef4444', '#f59e0b', '#8b5cf6', '#3b82f6', '#22c55e', '#6b7280'];
    return Object.entries(analyticsData.incidents.byType).map(([type, count], idx) => ({
      type,
      count,
      color: colors[idx % colors.length]
    }));
  };

  const getTopHazardsData = () => {
    if (!analyticsData?.jsas?.topHazards || analyticsData.jsas.topHazards.length === 0) {
      return []; // Return empty, not fake data
    }
    const colors = ['#ef4444', '#f59e0b', '#8b5cf6', '#3b82f6', '#22c55e'];
    // Limit to top 5 hazards and truncate names for readability
    return analyticsData.jsas.topHazards.slice(0, 5).map((item, idx) => {
      const fullName = item.hazard || item.name || '';
      // Truncate to 20 chars max for clean display
      const shortName = fullName.length > 20 ? fullName.substring(0, 18) + '...' : fullName;
      return {
        hazard: shortName,
        fullHazard: fullName, // Keep full name for tooltip
        count: item.count,
        color: colors[idx % colors.length]
      };
    });
  };

  const getJsaByCraftData = () => {
    if (!analyticsData?.jsas?.byCraft || Object.keys(analyticsData.jsas.byCraft).length === 0) {
      return []; // Return empty, not fake data
    }
    const hazardsByCraft = analyticsData.jsas.hazardsByCraft || {};
    return Object.entries(analyticsData.jsas.byCraft).map(([craft, count]) => ({
      craft,
      jsas: count,
      hazards: hazardsByCraft[craft] || 0
    }));
  };

  const getDivisionComparisonData = () => {
    if (!analyticsData?.divisions) return [];
    return analyticsData.divisions.map(div => ({
      name: div.name,
      incidents: div.incidents,
      jsas: div.jsas,
      audits: div.audits,
      users: div.users
    }));
  };

  // Handle drag start
  const handleDragStart = (e, injury) => {
    e.preventDefault();
    setDraggingId(injury.id);
    setSelectedInjury(injury);
    
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });
  };

  // Handle drag move
  const handleDragMove = (e) => {
    if (!draggingId) return;
    e.preventDefault();
    
    const container = document.getElementById('body-map-container');
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    
    // Clamp values between 0 and 100
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));
    
    setInjuries(prev => prev.map(injury => 
      injury.id === draggingId 
        ? { ...injury, x: `${clampedX}%`, y: `${clampedY}%` }
        : injury
    ));
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggingId(null);
  };

  // Human Body SVG Component - Using professional silhouette image with draggable dots
  const HumanBodyMap = () => (
    <div 
      id="body-map-container"
      className="relative w-full max-w-[500px] h-[450px] mx-auto bg-white rounded-lg select-none"
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
    >
      {/* Professional silhouette image - front and back view */}
      <img 
        src="https://customer-assets.emergentagent.com/job_formbuilder-7/artifacts/dj7vgm3e_injury%20silouette.png" 
        alt="Human body silhouette"
        className="w-full h-full object-contain pointer-events-none"
        style={{ 
          filter: 'contrast(1.1)',
          mixBlendMode: 'multiply'
        }}
        draggable={false}
      />
      
      {/* Draggable Injury Dots */}
      {injuries.map((injury) => (
        <div
          key={injury.id}
          className={`absolute cursor-grab transform -translate-x-1/2 -translate-y-1/2 transition-transform z-10 ${
            draggingId === injury.id ? 'cursor-grabbing scale-150' : 'hover:scale-125'
          }`}
          style={{ 
            left: injury.x, 
            top: injury.y,
          }}
          onMouseDown={(e) => handleDragStart(e, injury)}
          onTouchStart={(e) => handleDragStart(e, injury)}
          onClick={() => !draggingId && setSelectedInjury(injury)}
          title={`${injury.description} (Drag to reposition)`}
        >
          <div 
            className={`w-5 h-5 rounded-full border-2 border-white shadow-lg ${
              draggingId === injury.id ? 'ring-2 ring-blue-400 ring-offset-2' : ''
            }`}
            style={{ 
              backgroundColor: INJURY_COLORS[injury.type],
              boxShadow: `0 0 10px ${INJURY_COLORS[injury.type]}`,
            }}
          />
        </div>
      ))}
    </div>
  );

  // Circular Progress Component
  const CircularProgress = ({ value, max, label, color = '#22c55e', size = 100 }) => {
    const percentage = Math.round((value / max) * 100);
    const data = [{ value: percentage, fill: color }, { value: 100 - percentage, fill: '#e5e7eb' }];
    
    return (
      <div className="relative" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={size * 0.35}
              outerRadius={size * 0.45}
              startAngle={90}
              endAngle={-270}
              dataKey="value"
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-bold">{percentage}%</span>
          <span className="text-xs text-gray-500">{label}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="mb-2 -ml-2"
              onClick={() => navigate('/admin')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Safety Analytics</h1>
            <p className="text-gray-600">
              JSA, Audits, Incidents & Injury Tracking
              {analyticsData?.company?.name && (
                <span className="ml-2 text-blue-600">• {analyticsData.company.name}</span>
              )}
            </p>
            {loading && (
              <div className="flex items-center gap-2 text-blue-500 mt-1">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading live data...</span>
              </div>
            )}
            {error && (
              <p className="text-red-500 text-sm mt-1">Using sample data (API unavailable)</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant={timeRange === '30days' ? 'default' : 'outline'} size="sm" onClick={() => setTimeRange('30days')}>30 Days</Button>
            <Button variant={timeRange === '6months' ? 'default' : 'outline'} size="sm" onClick={() => setTimeRange('6months')}>6 Months</Button>
            <Button variant={timeRange === '1year' ? 'default' : 'outline'} size="sm" onClick={() => setTimeRange('1year')}>1 Year</Button>
          </div>
        </div>

        <Tabs defaultValue="jsa" className="w-full">
          <TabsList className="grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="jsa" className="flex items-center gap-2">
              <FileText className="w-4 h-4" /> JSA Analytics
            </TabsTrigger>
            <TabsTrigger value="audits" className="flex items-center gap-2">
              <Shield className="w-4 h-4" /> Audits
            </TabsTrigger>
            <TabsTrigger value="incidents" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Incidents
            </TabsTrigger>
            <TabsTrigger value="bodymap" className="flex items-center gap-2">
              <Activity className="w-4 h-4" /> Body Map
            </TabsTrigger>
          </TabsList>

          {/* JSA Tab */}
          <TabsContent value="jsa" className="space-y-6 mt-6">
            {/* JSA Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-green-100 text-sm">Total JSAs</p>
                      <p className="text-4xl font-bold mt-1">{liveJsaData.total}</p>
                      <p className="text-green-200 text-sm mt-2">{liveJsaData.active} active</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-blue-100 text-sm">Avg Hazards/JSA</p>
                      <p className="text-4xl font-bold mt-1">{liveJsaData.avgHazards}</p>
                      <p className="text-blue-200 text-sm mt-2">Per job analysis</p>
                    </div>
                    <Flame className="w-10 h-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-yellow-100 text-sm">Draft JSAs</p>
                      <p className="text-4xl font-bold mt-1">{liveJsaData.draft}</p>
                      <p className="text-yellow-200 text-sm mt-2">Pending completion</p>
                    </div>
                    <Clock className="w-10 h-10 text-yellow-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-purple-100 text-sm">Archived</p>
                      <p className="text-4xl font-bold mt-1">{liveJsaData.archived}</p>
                      <p className="text-purple-200 text-sm mt-2">Completed jobs</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* JSA Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>JSAs Created</CardTitle>
                  <CardDescription>Monthly JSA creation trend</CardDescription>
                </CardHeader>
                <CardContent>
                  {getJsaChartData().length === 0 ? (
                    <div className="flex items-center justify-center h-[300px] text-gray-500">
                      <div className="text-center">
                        <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No JSA data available</p>
                        <p className="text-sm">Create JSAs to see trends</p>
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getJsaChartData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="month" stroke="#6b7280" />
                        <YAxis stroke="#6b7280" allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="created" fill="#22c55e" name="JSAs Created" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Top Hazards Identified</CardTitle>
                  <CardDescription>Most common hazards across all JSAs</CardDescription>
                </CardHeader>
                <CardContent>
                  {getTopHazardsData().length === 0 ? (
                    <div className="flex items-center justify-center h-[300px] text-gray-500">
                      <div className="text-center">
                        <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No hazard data available</p>
                        <p className="text-sm">Create JSAs to see hazard analysis</p>
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getTopHazardsData()} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          type="number" 
                          stroke="#6b7280" 
                          allowDecimals={false}
                          tickFormatter={(value) => Math.floor(value)}
                        />
                        <YAxis 
                          dataKey="hazard" 
                          type="category" 
                          width={140} 
                          stroke="#6b7280"
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip 
                          formatter={(value, name, props) => [value, 'Count']}
                          labelFormatter={(label, payload) => payload?.[0]?.payload?.fullHazard || label}
                        />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                          {getTopHazardsData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* JSA by Craft */}
            <Card>
              <CardHeader>
                <CardTitle>JSAs & Hazards by Craft</CardTitle>
                <CardDescription>Number of JSAs and total hazards identified per craft</CardDescription>
              </CardHeader>
              <CardContent>
                {getJsaByCraftData().length === 0 ? (
                  <div className="flex items-center justify-center h-[250px] text-gray-500">
                    <div className="text-center">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No craft data available</p>
                      <p className="text-sm">Create JSAs to see activity by craft</p>
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={getJsaByCraftData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="craft" stroke="#6b7280" tick={{ fontSize: 12 }} />
                      <YAxis stroke="#6b7280" allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="jsas" fill="#3b82f6" name="JSAs" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="hazards" fill="#ef4444" name="Hazards Identified" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audits Tab */}
          <TabsContent value="audits" className="space-y-6 mt-6">
            {/* Audit Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-blue-100 text-sm">Total Audits</p>
                      <p className="text-4xl font-bold mt-1">{liveAuditData.total}</p>
                      <p className="text-blue-200 text-sm mt-2">This period</p>
                    </div>
                    <Shield className="w-10 h-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-green-100 text-sm">Average Score</p>
                      <p className="text-4xl font-bold mt-1">{liveAuditData.avgScore || 'N/A'}%</p>
                      <p className="text-green-200 text-sm mt-2">AI-powered scoring</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-red-100 text-sm">Open Findings</p>
                      <p className="text-4xl font-bold mt-1">{Object.values(liveAuditData.byStatus).reduce((a, b) => a + b, 0) - (liveAuditData.byStatus.completed || 0)}</p>
                      <p className="text-red-200 text-sm mt-2">Need resolution</p>
                    </div>
                    <XCircle className="w-10 h-10 text-red-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-purple-100 text-sm">Completed</p>
                      <p className="text-4xl font-bold mt-1">{liveAuditData.byStatus.completed || 0}</p>
                      <p className="text-purple-200 text-sm mt-2">Audits closed</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Audit Score Trend */}
              <Card>
                <CardHeader>
                  <CardTitle>Audit Score Trend</CardTitle>
                  <CardDescription>Monthly average scores vs target</CardDescription>
                </CardHeader>
                <CardContent>
                  {getAuditChartData().length === 0 ? (
                    <div className="flex items-center justify-center h-[300px] text-gray-500">
                      <div className="text-center">
                        <Shield className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No audit data available</p>
                        <p className="text-sm">Complete audits to see score trends</p>
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={getAuditChartData()}>
                        <defs>
                          <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="month" stroke="#6b7280" />
                        <YAxis domain={[0, 100]} stroke="#6b7280" />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="score" stroke="#22c55e" strokeWidth={2} fillOpacity={1} fill="url(#colorScore)" name="Actual Score" />
                        <Line type="monotone" dataKey="target" stroke="#ef4444" strokeDasharray="5 5" strokeWidth={2} name="Target" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Radar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Category Performance</CardTitle>
                  <CardDescription>Scores across audit categories</CardDescription>
                </CardHeader>
                <CardContent>
                  {liveAuditData.total === 0 ? (
                    <div className="flex items-center justify-center h-[300px] text-gray-500">
                      <div className="text-center">
                        <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No category data available</p>
                        <p className="text-sm">Complete audits to see category performance</p>
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <RadarChart data={getAuditCategoryData()}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="category" tick={{ fontSize: 11 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} />
                        <Radar name="Score" dataKey="score" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.5} />
                      </RadarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Findings Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Findings by Severity</CardTitle>
                <CardDescription>Distribution of audit findings</CardDescription>
              </CardHeader>
              <CardContent>
                {liveAuditData.total === 0 ? (
                  <div className="flex items-center justify-center h-[250px] text-gray-500">
                    <div className="text-center">
                      <CheckCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No findings data available</p>
                      <p className="text-sm">Complete audits to see findings breakdown</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getFindingsSeverityData()} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" stroke="#6b7280" allowDecimals={false} />
                        <YAxis dataKey="severity" type="category" stroke="#6b7280" width={80} />
                        <Tooltip />
                        <Bar dataKey="count" name="Findings">
                          {getFindingsSeverityData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Incidents Tab */}
          <TabsContent value="incidents" className="space-y-6 mt-6">
            {/* Incident Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-5">
                  <p className="text-green-100 text-sm">First Aid</p>
                  <p className="text-3xl font-bold mt-1">{liveIncidentData.bySeverity?.Low || 0}</p>
                  <p className="text-green-200 text-xs mt-1">Minor injuries</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
                <CardContent className="p-5">
                  <p className="text-yellow-100 text-sm">Medical Treatment</p>
                  <p className="text-3xl font-bold mt-1">{liveIncidentData.bySeverity?.Medium || 0}</p>
                  <p className="text-yellow-200 text-xs mt-1">Required treatment</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                <CardContent className="p-5">
                  <p className="text-red-100 text-sm">Recordable</p>
                  <p className="text-3xl font-bold mt-1">{(liveIncidentData.bySeverity?.High || 0) + (liveIncidentData.bySeverity?.Critical || 0)}</p>
                  <p className="text-red-200 text-xs mt-1">OSHA recordable</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-5">
                  <p className="text-blue-100 text-sm">Near Misses</p>
                  <p className="text-3xl font-bold mt-1">{liveNearMissData.total}</p>
                  <p className="text-blue-200 text-xs mt-1">Reported incidents</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-5">
                  <p className="text-purple-100 text-sm">Days Since Recordable</p>
                  <p className="text-3xl font-bold mt-1">{liveIncidentData.daysSinceRecordable ?? 'N/A'}</p>
                  <p className="text-purple-200 text-xs mt-1">Safety milestone</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Incident Trend */}
              <Card>
                <CardHeader>
                  <CardTitle>Incident Trend</CardTitle>
                  <CardDescription>Monthly incident breakdown by severity</CardDescription>
                </CardHeader>
                <CardContent>
                  {getIncidentChartData().length === 0 ? (
                    <div className="flex items-center justify-center h-[300px] text-gray-500">
                      <div className="text-center">
                        <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No incident data available</p>
                        <p className="text-sm">Incidents will appear here when reported</p>
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={getIncidentChartData()}>
                        <defs>
                          <linearGradient id="colorFirstAid" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="colorRecordable" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="colorNearMiss" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="month" stroke="#6b7280" />
                        <YAxis stroke="#6b7280" allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="nearMiss" stroke="#3b82f6" fill="url(#colorNearMiss)" name="Near Miss" />
                        <Area type="monotone" dataKey="firstAid" stroke="#22c55e" fill="url(#colorFirstAid)" name="First Aid" />
                        <Area type="monotone" dataKey="recordable" stroke="#ef4444" fill="url(#colorRecordable)" name="Recordable" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Incident Types */}
              <Card>
                <CardHeader>
                  <CardTitle>Incident Types</CardTitle>
                  <CardDescription>Distribution by incident category</CardDescription>
                </CardHeader>
                <CardContent>
                  {getIncidentTypeData().length === 0 ? (
                    <div className="flex items-center justify-center h-[300px] text-gray-500">
                      <div className="text-center">
                        <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No incident type data</p>
                        <p className="text-sm">Report incidents to see distribution</p>
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={getIncidentTypeData()}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={3}
                          dataKey="count"
                          nameKey="type"
                          label={({ type, percent }) => `${type}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {getIncidentTypeData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Safety Rate Cards - CLICKABLE to input data */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card 
                className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-300"
                onClick={() => setShowMetricsDialog(true)}
              >
                <CardContent className="p-6 flex flex-col items-center">
                  <CircularProgress 
                    value={safetyMetrics.trirRate || 0} 
                    max={3} 
                    label="TRIR" 
                    color={safetyMetrics.trirRate < 3 ? "#22c55e" : "#ef4444"} 
                  />
                  <p className="mt-3 font-semibold">Total Recordable Rate</p>
                  <p className="text-sm text-gray-500">Target: &lt; 3.0</p>
                  <p className="text-xs text-blue-500 mt-1">Click to update</p>
                </CardContent>
              </Card>
              <Card 
                className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-300"
                onClick={() => setShowMetricsDialog(true)}
              >
                <CardContent className="p-6 flex flex-col items-center">
                  <CircularProgress 
                    value={safetyMetrics.dartRate || 0} 
                    max={1} 
                    label="DART" 
                    color={safetyMetrics.dartRate < 1 ? "#22c55e" : "#ef4444"} 
                  />
                  <p className="mt-3 font-semibold">DART Rate</p>
                  <p className="text-sm text-gray-500">Target: &lt; 1.0</p>
                  <p className="text-xs text-blue-500 mt-1">Click to update</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 flex flex-col items-center">
                  <CircularProgress value={0} max={1} label="LTI" color="#22c55e" />
                  <p className="mt-3 font-semibold">Lost Time Injuries</p>
                  <p className="text-sm text-gray-500">This period: 0</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 flex flex-col items-center">
                  <CircularProgress value={liveNearMissData.total > 0 ? 92 : 0} max={100} label="Rate" color="#3b82f6" />
                  <p className="mt-3 font-semibold">Near Miss Reporting</p>
                  <p className="text-sm text-gray-500">Target: 85%</p>
                </CardContent>
              </Card>
            </div>

            {/* Division Comparison - Only show if there are multiple divisions */}
            {getDivisionComparisonData().length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle>Division Comparison</CardTitle>
                  <CardDescription>Safety metrics across all divisions</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getDivisionComparisonData()} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="jsas" fill="#22c55e" name="JSAs" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="audits" fill="#3b82f6" name="Audits" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="incidents" fill="#ef4444" name="Incidents" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Body Map Tab */}
          <TabsContent value="bodymap" className="space-y-6 mt-6">
            {/* Body Map - Full Width */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Injury Body Map</CardTitle>
                    <CardDescription>Front and back view - Click on dots to view injury details. {isAdmin && 'Drag dots to reposition.'}</CardDescription>
                  </div>
                  {isAdmin && (
                    <Button onClick={() => setShowAddInjuryDialog(true)} className="bg-red-600 hover:bg-red-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Injury
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col lg:flex-row gap-6 items-start">
                  {/* Body silhouette */}
                  <div className="flex-1">
                    <HumanBodyMap />
                    
                    {/* Front/Back Labels */}
                    <div className="flex justify-around mt-2 text-sm text-gray-500 font-medium">
                      <span>← FRONT</span>
                      <span>BACK →</span>
                    </div>
                  </div>
                  
                  {/* Legend and Selected Injury */}
                  <div className="lg:w-64 space-y-4">
                    {/* Legend */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3">Injury Severity</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-green-500 shadow-lg" style={{ boxShadow: '0 0 8px #22c55e' }} />
                          <span className="text-sm">First Aid</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-yellow-500 shadow-lg" style={{ boxShadow: '0 0 8px #f59e0b' }} />
                          <span className="text-sm">Medical Treatment</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-red-500 shadow-lg" style={{ boxShadow: '0 0 8px #ef4444' }} />
                          <span className="text-sm">Recordable</span>
                        </div>
                      </div>
                    </div>

                    {/* Selected Injury Detail */}
                    {selectedInjury ? (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: INJURY_COLORS[selectedInjury.type] }}
                          />
                          <Badge variant={
                            selectedInjury.type === 'recordable' ? 'destructive' : 
                            selectedInjury.type === 'hospital' || selectedInjury.type === 'medical' ? 'default' : 'secondary'
                          }>
                            {selectedInjury.type === 'firstAid' ? 'First Aid' : 
                             selectedInjury.type === 'hospital' || selectedInjury.type === 'medical' ? 'Medical Treatment' : 'Recordable'}
                          </Badge>
                        </div>
                        <p className="font-semibold">{selectedInjury.description}</p>
                        {selectedInjury.employeeName && (
                          <p className="text-sm text-gray-700">Employee: {selectedInjury.employeeName}</p>
                        )}
                        <p className="text-sm text-gray-500">Body Part: {selectedInjury.part}</p>
                        <p className="text-sm text-gray-500">View: {selectedInjury.view}</p>
                        <p className="text-sm text-gray-500">Date: {selectedInjury.date}</p>
                        {selectedInjury.source === 'incident' && (
                          <p className="text-xs text-blue-600 mt-1">📋 From Incident Report</p>
                        )}
                        {isAdmin && (
                          <div className="mt-3 flex gap-2">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteInjury(selectedInjury.id)}
                              className="w-full"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        )}
                        <p className="text-xs text-blue-500 mt-2 italic">💡 Drag dots to reposition</p>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-100 rounded-lg border border-dashed border-gray-300 text-center">
                        <p className="text-sm text-gray-500">Click on a dot to view details</p>
                        <p className="text-xs text-gray-400 mt-1">Drag dots to reposition</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Injury Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle>Injury Statistics by Body Part</CardTitle>
                  <CardDescription>Distribution of injuries across body regions</CardDescription>
                </CardHeader>
                <CardContent>
                  {injuries.length === 0 ? (
                    <div className="flex items-center justify-center h-[200px] text-gray-500">
                      <div className="text-center">
                        <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No injury data available</p>
                        <p className="text-sm">Body map injuries will appear here when recorded</p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {(() => {
                        // Calculate actual stats from injuries
                        const bodyPartCounts = {};
                        injuries.forEach(injury => {
                          const part = injury.bodyPart || injury.part || 'Other';
                          bodyPartCounts[part] = (bodyPartCounts[part] || 0) + 1;
                        });
                        const total = injuries.length;
                        const colors = ['#ef4444', '#f59e0b', '#8b5cf6', '#3b82f6', '#22c55e', '#ec4899'];
                        return Object.entries(bodyPartCounts)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 6)
                          .map(([part, count], idx) => ({
                            part,
                            count,
                            percent: Math.round((count / total) * 100),
                            color: colors[idx % colors.length]
                          }));
                      })().map((item) => (
                        <div key={item.part} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">{item.part}</span>
                            <span className="text-lg font-bold" style={{ color: item.color }}>{item.count}</span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all"
                              style={{ width: `${item.percent}%`, backgroundColor: item.color }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{item.percent}% of all injuries</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Injury Summary</CardTitle>
                  <CardDescription>Total injuries by severity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-6 bg-green-50 rounded-lg">
                      <div className="text-4xl font-bold text-green-600">{liveIncidentData.bySeverity.Low || 0}</div>
                      <div className="text-sm text-gray-600 mt-1">First Aid</div>
                      <div className="text-xs text-green-600 mt-2">No lost time</div>
                    </div>
                    <div className="text-center p-6 bg-yellow-50 rounded-lg">
                      <div className="text-4xl font-bold text-yellow-600">{liveIncidentData.bySeverity.Medium || 0}</div>
                      <div className="text-sm text-gray-600 mt-1">Medical Treatment</div>
                      <div className="text-xs text-yellow-600 mt-2">Required treatment</div>
                    </div>
                    <div className="text-center p-6 bg-red-50 rounded-lg">
                      <div className="text-4xl font-bold text-red-600">{(liveIncidentData.bySeverity.High || 0) + (liveIncidentData.bySeverity.Critical || 0)}</div>
                      <div className="text-sm text-gray-600 mt-1">Recordable</div>
                      <div className="text-xs text-red-600 mt-2">OSHA reportable</div>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Days Since Last Recordable</span>
                      <span className="text-2xl font-bold text-blue-600">{liveIncidentData.daysSinceRecordable ?? 'N/A'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Injuries Table */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Injury Log</CardTitle>
                <CardDescription>Last 10 reported injuries</CardDescription>
              </CardHeader>
              <CardContent>
                {injuries.length === 0 ? (
                  <div className="flex items-center justify-center h-[200px] text-gray-500">
                    <div className="text-center">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No injury records</p>
                      <p className="text-sm">Add injuries to the body map to see them here</p>
                    </div>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Date</th>
                        <th className="text-left py-3 px-4">Body Part</th>
                        <th className="text-left py-3 px-4">Description</th>
                        <th className="text-left py-3 px-4">Severity</th>
                        <th className="text-left py-3 px-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {injuries.slice(0, 10).map((injury) => (
                        <tr key={injury.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">{injury.date || new Date(injury.createdAt).toLocaleDateString()}</td>
                          <td className="py-3 px-4 capitalize">{(injury.bodyPart || injury.part || 'Unknown').replace(/([A-Z])/g, ' $1').trim()}</td>
                          <td className="py-3 px-4">{injury.description || '-'}</td>
                          <td className="py-3 px-4">
                            <Badge 
                              className={
                                injury.type === 'recordable' ? 'bg-red-100 text-red-700' : 
                                injury.type === 'hospital' || injury.type === 'medical' ? 'bg-yellow-100 text-yellow-700' : 
                                'bg-green-100 text-green-700'
                              }
                            >
                              {injury.type === 'firstAid' ? 'First Aid' : 
                               injury.type === 'hospital' || injury.type === 'medical' ? 'Medical Treatment' : 
                               injury.type === 'recordable' ? 'Recordable' : 'First Aid'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline">{injury.status || 'Active'}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Injury Dialog */}
      <Dialog open={showAddInjuryDialog} onOpenChange={setShowAddInjuryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Injury to Body Map</DialogTitle>
            <DialogDescription>
              Manually add an injury record to track on the body map visualization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Body Part *</Label>
              <Select 
                value={newInjury.bodyPart} 
                onValueChange={(value) => setNewInjury({...newInjury, bodyPart: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select body part..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="head">Head</SelectItem>
                  <SelectItem value="neck">Neck</SelectItem>
                  <SelectItem value="leftShoulder">Left Shoulder</SelectItem>
                  <SelectItem value="rightShoulder">Right Shoulder</SelectItem>
                  <SelectItem value="leftArm">Left Arm</SelectItem>
                  <SelectItem value="rightArm">Right Arm</SelectItem>
                  <SelectItem value="leftHand">Left Hand/Fingers</SelectItem>
                  <SelectItem value="rightHand">Right Hand/Fingers</SelectItem>
                  <SelectItem value="chest">Chest</SelectItem>
                  <SelectItem value="abdomen">Abdomen</SelectItem>
                  <SelectItem value="upperBack">Upper Back</SelectItem>
                  <SelectItem value="lowerBack">Lower Back</SelectItem>
                  <SelectItem value="hip">Hip</SelectItem>
                  <SelectItem value="leftLeg">Left Leg</SelectItem>
                  <SelectItem value="rightLeg">Right Leg</SelectItem>
                  <SelectItem value="leftKnee">Left Knee</SelectItem>
                  <SelectItem value="rightKnee">Right Knee</SelectItem>
                  <SelectItem value="leftAnkle">Left Ankle</SelectItem>
                  <SelectItem value="rightAnkle">Right Ankle</SelectItem>
                  <SelectItem value="leftFoot">Left Foot</SelectItem>
                  <SelectItem value="rightFoot">Right Foot</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Injury Severity</Label>
              <Select 
                value={newInjury.injuryType} 
                onValueChange={(value) => setNewInjury({...newInjury, injuryType: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="firstAid">First Aid Only (Green)</SelectItem>
                  <SelectItem value="medical">Medical Treatment (Yellow)</SelectItem>
                  <SelectItem value="recordable">Recordable/Serious (Red)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Employee Name (Optional)</Label>
              <Input
                value={newInjury.employeeName}
                onChange={(e) => setNewInjury({...newInjury, employeeName: e.target.value})}
                placeholder="Enter employee name"
              />
            </div>

            <div className="space-y-2">
              <Label>Incident Date</Label>
              <Input
                type="date"
                value={newInjury.incidentDate}
                onChange={(e) => setNewInjury({...newInjury, incidentDate: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={newInjury.description}
                onChange={(e) => setNewInjury({...newInjury, description: e.target.value})}
                placeholder="Describe the injury..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddInjuryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddInjury} disabled={savingInjury} className="bg-red-600 hover:bg-red-700">
              {savingInjury ? 'Adding...' : 'Add Injury'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Safety Metrics Dialog (TRIR/DART Calculator) */}
      <Dialog open={showMetricsDialog} onOpenChange={setShowMetricsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Safety Metrics Calculator
            </DialogTitle>
            <DialogDescription>
              Enter your workforce data to calculate TRIR and DART rates for the current period.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Workforce Data */}
            <div className="bg-blue-50 p-4 rounded-lg space-y-4">
              <h4 className="font-semibold text-blue-900">Workforce Data</h4>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="employeeCount" className="text-sm">Employees</Label>
                  <Input
                    id="employeeCount"
                    type="number"
                    min="0"
                    value={metricsInput.employeeCount}
                    onChange={(e) => setMetricsInput({...metricsInput, employeeCount: parseInt(e.target.value) || 0})}
                    className="bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="hoursPerWeek" className="text-sm">Hours/Week</Label>
                  <Input
                    id="hoursPerWeek"
                    type="number"
                    min="0"
                    step="0.5"
                    value={metricsInput.hoursPerWeek}
                    onChange={(e) => setMetricsInput({...metricsInput, hoursPerWeek: parseFloat(e.target.value) || 0})}
                    className="bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="weeksInPeriod" className="text-sm">Weeks</Label>
                  <Input
                    id="weeksInPeriod"
                    type="number"
                    min="0"
                    step="0.5"
                    value={metricsInput.weeksInPeriod}
                    onChange={(e) => setMetricsInput({...metricsInput, weeksInPeriod: parseFloat(e.target.value) || 0})}
                    className="bg-white"
                  />
                </div>
              </div>
              
              <div className="text-sm text-blue-700 bg-blue-100 p-2 rounded">
                <strong>Total Hours:</strong> {calculatePreview().totalHours.toLocaleString()} hours
              </div>
            </div>

            {/* Incident Data */}
            <div className="bg-red-50 p-4 rounded-lg space-y-4">
              <h4 className="font-semibold text-red-900">Incident Data (Manual Entry)</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="recordableInjuries" className="text-sm">Recordable Injuries</Label>
                  <Input
                    id="recordableInjuries"
                    type="number"
                    min="0"
                    value={metricsInput.recordableInjuries}
                    onChange={(e) => setMetricsInput({...metricsInput, recordableInjuries: parseInt(e.target.value) || 0})}
                    className="bg-white"
                  />
                  <p className="text-xs text-gray-500">OSHA recordable cases</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="dartCases" className="text-sm">DART Cases</Label>
                  <Input
                    id="dartCases"
                    type="number"
                    min="0"
                    value={metricsInput.dartCases}
                    onChange={(e) => setMetricsInput({...metricsInput, dartCases: parseInt(e.target.value) || 0})}
                    className="bg-white"
                  />
                  <p className="text-xs text-gray-500">Days Away/Restricted/Transfer</p>
                </div>
              </div>
            </div>

            {/* Calculated Rates Preview */}
            <div className="bg-gray-100 p-4 rounded-lg">
              <h4 className="font-semibold mb-3">Calculated Rates (Preview)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-3 rounded-lg text-center">
                  <p className="text-3xl font-bold" style={{ color: parseFloat(calculatePreview().trir) < 3 ? '#22c55e' : '#ef4444' }}>
                    {calculatePreview().trir}
                  </p>
                  <p className="text-sm text-gray-600">TRIR</p>
                  <p className="text-xs text-gray-400">Target: &lt; 3.0</p>
                </div>
                <div className="bg-white p-3 rounded-lg text-center">
                  <p className="text-3xl font-bold" style={{ color: parseFloat(calculatePreview().dart) < 1 ? '#22c55e' : '#ef4444' }}>
                    {calculatePreview().dart}
                  </p>
                  <p className="text-sm text-gray-600">DART</p>
                  <p className="text-xs text-gray-400">Target: &lt; 1.0</p>
                </div>
              </div>
            </div>

            {/* Formula Info */}
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
              <p><strong>TRIR Formula:</strong> (Recordable Injuries × 200,000) ÷ Total Hours Worked</p>
              <p><strong>DART Formula:</strong> (DART Cases × 200,000) ÷ Total Hours Worked</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMetricsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveMetrics} disabled={savingMetrics} className="bg-blue-600 hover:bg-blue-700">
              {savingMetrics ? 'Saving...' : 'Save Metrics'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SafetyAnalyticsMockup;
